import { faPlus, faSparkles } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material';
import React, { useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';

import SearchFilled from '../../assets/search-filled.svg';
import { CUSTOM_SEGMENTS_EDIT_URL, CUSTOM_SEGMENTS_NEW_URL } from '../../constants/routeUrls';
import useAudiences from '../../hooks/useAudiences';
import posthog from '../../util/posthog';
import { headerStyles } from '../Contracts/ContractsTable';
import AiAudienceSearch from './AiAudienceSearch';

function CustomSegmentsList() {
  const [tempNameFilter, setTempNameFilter] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [aiSearchOpen, setAiSearchOpen] = useState(false);
  const navigate = useNavigate();

  const { data, isLoading, page, setPage, perPage, setPerPage } = useAudiences(nameFilter);

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      setNameFilter(tempNameFilter);
    }
  };

  const headerStylesCss = headerStyles();

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const totalItems = data?.pagination?.total_items || 0;

  return (
    <Box p={2} sx={{ overflowX: 'hidden' }}>
      <Box sx={{ display: 'flex', justifyContent: 'end', flexDirection: 'row' }}>
        <Stack spacing={1} direction="row" alignItems="center">
          <Autocomplete
            freeSolo
            value={tempNameFilter}
            options={[]}
            size="small"
            style={{ width: 160 }}
            onChange={(e, value, reason) => {
              if (reason === 'clear') setNameFilter('');
            }}
            clearIcon={isLoading ? <CircularProgress color="inherit" size={16} /> : undefined}
            renderInput={(params) => (
              <TextField
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...params}
                sx={{ backgroundColor: 'white' }}
                variant="outlined"
                size="small"
                placeholder="Search"
                onKeyDown={handleKeyPress}
                onChange={(e) => setTempNameFilter(e.target.value)}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: <img src={SearchFilled} alt="Search" />,
                }}
              />
            )}
          />
          <Button
            size="small"
            variant={aiSearchOpen ? 'contained' : 'outlined'}
            startIcon={<FontAwesomeIcon icon={faSparkles} />}
            aria-expanded={aiSearchOpen}
            aria-controls="ai-audience-search"
            onClick={() =>
              setAiSearchOpen((prev) => {
                const next = !prev;
                // Only the open transition is a signal of intent to use the
                // feature; closing is not interesting.
                if (next) posthog.capture('ai_audience_search_opened', { context: 'custom_segments_list' });
                return next;
              })
            }
          >
            Search by AI Description
          </Button>
          <Button
            onClick={() => navigate(CUSTOM_SEGMENTS_NEW_URL)}
            size="small"
            startIcon={<FontAwesomeIcon icon={faPlus} />}
          >
            New Segment
          </Button>
        </Stack>
      </Box>
      <Box py={1}>
          <TableContainer className={headerStylesCss.root} sx={{ maxHeight: 'calc(100vh - 250px)', overflowY: 'auto', width: '100%', overflowX: 'hidden' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Associated Ad Groups</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading &&
                  [...Array(50)].map((item, idx) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <TableRow key={idx} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell scope="row">
                        <Skeleton animation="wave" variant="text" sx={{ fontSize: '1rem' }} />
                      </TableCell>
                      <TableCell scope="row">
                        <Skeleton animation="wave" variant="text" sx={{ fontSize: '1rem' }} />
                      </TableCell>
                    </TableRow>
                  ))}
                {data?.collection?.map((customSegment) => (
                  <TableRow
                    key={`table-row-${customSegment.id}`}
                    onClick={() => navigate(generatePath(CUSTOM_SEGMENTS_EDIT_URL, { id: customSegment.id }))}
                  >
                    <TableCell>{customSegment.name}</TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {customSegment.associated_adgroups && customSegment.associated_adgroups.length > 0 ? (
                        <Tooltip
                          title={
                            <div style={{ 
                              display: 'grid', 
                              gridTemplateColumns: 'repeat(10, 1fr)', 
                              gap: '8px',
                              maxWidth: '600px',
                              padding: '4px'
                            }}>
                              {customSegment.associated_adgroups.map((id) => (
                                <div key={id} style={{ textAlign: 'center' }}>
                                  {id}
                                </div>
                              ))}
                            </div>
                          }
                          arrow
                          componentsProps={{
                            tooltip: {
                              sx: {
                                maxWidth: '620px',
                                backgroundColor: 'rgba(97, 97, 97, 0.92)',
                                pointerEvents: 'auto'
                              }
                            }
                          }}
                        >
                          <Chip 
                            label={`${customSegment.associated_adgroups.length} Ad Group${customSegment.associated_adgroups.length !== 1 ? 's' : ''}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </Tooltip>
                      ) : (
                        <span style={{ color: '#999' }}>No Ad Groups</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        <TablePagination
          component="div"
          count={totalItems}
          page={page - 1}
          onPageChange={handleChangePage}
          rowsPerPage={perPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Box>
      <Collapse in={aiSearchOpen} id="ai-audience-search">
        <Divider sx={{ mb: 2 }} />
        <AiAudienceSearch
          onUseSegment={(segment) => {
            setTempNameFilter(segment.name);
            setNameFilter(segment.name);
            setAiSearchOpen(false);
          }}
        />
      </Collapse>
    </Box>
  );
}

export default CustomSegmentsList;
